import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Autocomplete,
  TextField,
} from "@mui/material";
import { PersonOff, SwapHoriz } from "@mui/icons-material";
import { useImpersonation } from "../../context/ImpersonationContext";
import { useProfile } from "../../context/ProfileContext";
import layout from "../../constants/layout";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import { groups } from "../../constants/auth";

const IMPERSONATION_CONFIG = {
  parent: {
    noun: "Parent",
    dashboardNoun: "parent",
    placeholder: "Search parents",
    unknownLabel: "Unknown Parent",
  },
  student: {
    noun: "Student",
    dashboardNoun: "student",
    placeholder: "Search students",
    unknownLabel: "Unknown Student",
  },
};

function getUserType(userType) {
  return userType === "student" ? "student" : "parent";
}

function mapParentStudentOption(student) {
  const label =
    `${student.first_name || ""} ${student.last_name || ""}`.trim() ||
    student.email ||
    "Unknown Student";

  return {
    uuid: student.uuid,
    label,
    data: {
      ...student,
      user: {
        first_name: student.first_name || "",
        last_name: student.last_name || "",
        email: student.email || "",
      },
    },
  };
}

export default function ImpersonationBanner({ userType, mode }) {
  const resolvedUserType = getUserType(userType ?? mode ?? "parent");
  const config = IMPERSONATION_CONFIG[resolvedUserType];
  const { profile } = useProfile();
  const {
    impersonatedProfile,
    impersonatedParent,
    impersonatedStudent,
    setImpersonation,
    clearImpersonation,
    isImpersonating,
    isReadOnlyImpersonation,
  } = useImpersonation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const activeImpersonation =
    impersonatedProfile ||
    (resolvedUserType === "student" ? impersonatedStudent : impersonatedParent);
  const selectedSummary = useMemo(() => {
    if (!selectedProfile?.data) {
      return "";
    }

    const firstName =
      selectedProfile.data.user?.first_name ||
      selectedProfile.data.first_name ||
      selectedProfile.data.user__first_name ||
      "";
    const lastName =
      selectedProfile.data.user?.last_name ||
      selectedProfile.data.last_name ||
      selectedProfile.data.user__last_name ||
      "";
    const email =
      selectedProfile.data.user?.email ||
      selectedProfile.data.email ||
      selectedProfile.data.user__email ||
      "";

    return `${firstName} ${lastName}`.trim() + (email ? ` (${email})` : "");
  }, [selectedProfile]);

  const isEmployee = profile?.groups?.includes(groups.EMPLOYEES);
  const isParent =
    profile?.groups?.includes(groups.PARENTS) && !isEmployee;
  const parentProfileUuid = profile?.parent?.uuid;
  const canUseStudentImpersonation =
    resolvedUserType === "student" && (isEmployee || isParent);
  const canUseParentImpersonation =
    resolvedUserType === "parent" && isEmployee;

  const handleOpenDialog = () => {
    setSelectedProfile(activeImpersonation);
    setSearchInput("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
    setSearchInput("");
  };

  const handleSaveImpersonation = () => {
    setImpersonation(selectedProfile);
    handleCloseDialog();
    if (resolvedUserType === "student") {
      window.location.assign("/student/dashboard/");
    }
  };

  const handleClearImpersonation = () => {
    clearImpersonation();
    handleCloseDialog();
    window.location.assign("/dashboard/");
  };

  useEffect(() => {
    if (!dialogOpen) {
      setOptions([]);
      setIsLoadingOptions(false);
      return undefined;
    }

    if (isParent && resolvedUserType === "student" && parentProfileUuid) {
      let isMounted = true;
      const loadParentStudents = async () => {
        setIsLoadingOptions(true);
        try {
          const response = await api.get(
            apiUrls.parent(parentProfileUuid).students()
          );
          if (!isMounted) {
            return;
          }
          const students = (response.data?.data?.students || []).map(mapParentStudentOption);
          const search = searchInput.trim().toLowerCase();
          const filtered = search
            ? students.filter((option) =>
                option.label.toLowerCase().includes(search)
              )
            : students;
          if (
            activeImpersonation &&
            !filtered.some((option) => option.uuid === activeImpersonation.uuid)
          ) {
            setOptions([activeImpersonation, ...filtered]);
          } else {
            setOptions(filtered);
          }
        } catch (error) {
          if (isMounted) {
            setOptions(activeImpersonation ? [activeImpersonation] : []);
          }
        } finally {
          if (isMounted) {
            setIsLoadingOptions(false);
          }
        }
      };
      loadParentStudents();
      return () => {
        isMounted = false;
      };
    }

    if (!isEmployee) {
      setOptions([]);
      setIsLoadingOptions(false);
      return undefined;
    }

    let isMounted = true;
    const search = searchInput.trim();
    const timeoutId = setTimeout(async () => {
      setIsLoadingOptions(true);
      try {
        const response = await api.get(apiUrls.admin.impersonationOptions(), {
          params: {
            user_type: resolvedUserType,
            search_input: search || undefined,
            limit: 25,
          },
        });
        if (!isMounted) {
          return;
        }

        const fetchedOptions = response.data?.data || [];
        if (
          activeImpersonation &&
          !fetchedOptions.some((option) => option.uuid === activeImpersonation.uuid)
        ) {
          setOptions([activeImpersonation, ...fetchedOptions]);
        } else {
          setOptions(fetchedOptions);
        }
      } catch (error) {
        if (isMounted) {
          setOptions(activeImpersonation ? [activeImpersonation] : []);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOptions(false);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [
    activeImpersonation,
    dialogOpen,
    isEmployee,
    isParent,
    parentProfileUuid,
    resolvedUserType,
    searchInput,
  ]);

  if (!canUseStudentImpersonation && !canUseParentImpersonation) {
    return null;
  }

  if (isParent && resolvedUserType === "student" && !isImpersonating) {
    return null;
  }

  const bannerMessage = isReadOnlyImpersonation
    ? `Viewing student dashboard (read-only): ${activeImpersonation?.label || config.unknownLabel}`
    : `Impersonating: ${activeImpersonation?.label || config.unknownLabel}`;

  return (
    <>
      {isImpersonating && (
        <Box
          sx={(theme) => ({
            position: "relative",
            top: `${layout.navBarHeight}rem`,
            left: 0,
            right: 0,
            bgcolor: "warning.main",
            color: "warning.contrastText",
            py: 1,
            px: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: theme.zIndex.drawer - 1,
            gap: 0.2,
            height: "50px",
          })}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
            <Typography variant="body2" fontWeight="bold">
              {bannerMessage}
            </Typography>
            <Chip
              label={`Change ${config.noun}`}
              size="small"
              icon={<SwapHoriz />}
              onClick={handleOpenDialog}
              sx={{
                bgcolor: "warning.dark",
                color: "warning.contrastText",
                "&:hover": {
                  bgcolor: "warning.dark",
                  opacity: 0.8,
                },
              }}
            />
          </Box>
        </Box>
      )}

      {isEmployee && !isImpersonating && canUseParentImpersonation && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
          }}
        >
          <Chip
            label={`Impersonate ${config.noun}`}
            color="info"
            icon={<SwapHoriz />}
            onClick={handleOpenDialog}
          />
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isImpersonating ? "Change" : "Select"} {config.noun}{" "}
          {isParent ? "View" : "Impersonation"}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {isParent ? (
              <>
                Select which of your students to view on the student dashboard.
                This view is read-only; you cannot change your student&apos;s
                data from here.
              </>
            ) : (
              <>
                When impersonating a {config.dashboardNoun}, you will see the{" "}
                {config.dashboardNoun} dashboard and related data as if you were
                that {config.dashboardNoun}. This is for support and testing
                purposes only.
              </>
            )}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={options}
              value={selectedProfile}
              onChange={(_, nextValue) => setSelectedProfile(nextValue)}
              inputValue={searchInput}
              onInputChange={(_, value) => setSearchInput(value)}
              getOptionLabel={(option) => option?.label || ""}
              isOptionEqualToValue={(option, value) => option?.uuid === value?.uuid}
              loading={isLoadingOptions}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Select ${config.noun}`}
                  placeholder={config.placeholder}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Box>
          {selectedProfile && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedProfile.label}
              </Typography>
              {selectedSummary && (
                <Typography variant="caption" color="text.secondary">
                  {selectedSummary}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isImpersonating && (
            <Button
              onClick={handleClearImpersonation}
              color="error"
              startIcon={<PersonOff />}
            >
              Stop {isParent ? "Viewing" : "Impersonating"}
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveImpersonation}
            variant="contained"
            disabled={!selectedProfile}
          >
            {isImpersonating ? "Change" : "Start"}{" "}
            {isParent ? "Viewing" : "Impersonation"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
